/**
 * EnvironmentalMeasurement List Page
 * Consulta, filtra, edita em volume e exporta as medições ambientais (#351)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle, useVeredito } from '../../authorization/useVeredito';
import { environmentalMeasurementService } from '../../services/environmentalMeasurementService';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { measurementEquipmentService } from '../../services/measurementEquipmentService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { OccupationalRiskStatus } from '../../types/occupationalRisk';
import {
  EnvironmentalMeasurement,
  EnvironmentalMeasurementFilters,
} from '../../types/environmentalMeasurement';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import {
  formatarNumeroDaMedicao,
  situacaoDaMedicao,
} from '../../components/measurements/limiteDaSilica';
import {
  EnvironmentalMeasurementFiltersComponent,
  type EnvironmentalMeasurementFiltersData,
  type NamedOption,
} from './components/EnvironmentalMeasurementFilters';

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

// Nulo é a avaliação de periculosidade, que não mede grandeza nenhuma: mostrar "0" ali
// pareceria medição feita e zerada, que é pior que não aparecer (#424). Até 4 casas, como o
// laudo e o e-mail: com 3, o 0,6667 do limite da sílica apareceria "0,667" (#459).
const formatValue = (value: number | null): string =>
  value != null && Number.isFinite(Number(value)) ? formatarNumeroDaMedicao(Number(value)) : '—';

/**
 * Risco com fórmula tem limite calculado gravado; nos demais, o "excede" é o que o técnico marcou.
 * Os dois chips podem coexistir na mesma lista, por isso a origem aparece escrita embaixo.
 */
const chipDaSituacao = (m: EnvironmentalMeasurement): React.ReactNode => {
  if (m.calculatedToleranceLimit != null && m.value != null) {
    const situacao = situacaoDaMedicao({
      valor: Number(m.value),
      limite: Number(m.calculatedToleranceLimit),
    });
    const chip = {
      ABAIXO_DA_ACAO: { label: 'Abaixo do nível de ação', color: 'success', filled: false },
      ACIMA_DA_ACAO: { label: 'Acima do nível de ação', color: 'warning', filled: true },
      ACIMA_DO_LIMITE: { label: 'Acima do limite', color: 'error', filled: true },
    } as const;
    const { label, color, filled } = chip[situacao];

    return (
      <>
        <Chip label={label} color={color} variant={filled ? 'filled' : 'outlined'} size="small" />
        <Typography variant="caption" color="text.secondary" display="block">
          Calculado pelo sistema
        </Typography>
      </>
    );
  }

  return (
    <>
      <Chip
        label={m.exceedsLimit ? 'Excede o limite' : 'Dentro do limite'}
        color={m.exceedsLimit ? 'error' : 'success'}
        variant={m.exceedsLimit ? 'filled' : 'outlined'}
        size="small"
      />
      <Typography variant="caption" color="text.secondary" display="block">
        Informado por quem mediu
      </Typography>
    </>
  );
};

const columns: DataTableColumn<EnvironmentalMeasurement>[] = [
  {
    id: 'measuredAt',
    label: 'Data',
    render: (m) => <Typography variant="body2">{formatDate(m.measuredAt)}</Typography>,
  },
  {
    id: 'risk',
    label: 'Risco',
    primary: true,
    render: (m) => (
      <>
        <Typography variant="body2" fontWeight="medium">
          {m.riskFactorName ?? '—'}
        </Typography>
        {m.technique && (
          <Typography variant="caption" color="text.secondary">
            {m.technique}
            {m.exposureTime ? ` · ${m.exposureTime}` : ''}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'value',
    label: 'Valor medido',
    align: 'right',
    render: (m) => (
      <>
        <Typography variant="body2" fontWeight="medium">
          {formatValue(m.value)}
          {m.unit ? ` ${m.unit}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {/* O limite aplicado é o desta medição; o do risco só aparece na ausência dele (#417) */}
          {m.riskIsHazardous
            ? (m.hazardousAnnex ?? 'NR-16: sem limite de tolerância')
            : m.calculatedToleranceLimit != null
              ? `Limite calculado: ${m.appliedToleranceLimit ?? formatarNumeroDaMedicao(Number(m.calculatedToleranceLimit))} (quartzo ${formatarNumeroDaMedicao(Number(m.quartzPercent ?? 0))}%)`
              : m.appliedToleranceLimit
                ? `Limite aplicado: ${m.appliedToleranceLimit}`
                : (m.toleranceLimit ? `Limite: ${m.toleranceLimit}` : 'Limite não cadastrado')}
        </Typography>
      </>
    ),
  },
  {
    id: 'exceedsLimit',
    label: 'Situação',
    align: 'center',
    render: (m) => {
      // Periculosidade não tem limite para exceder: dizer "Dentro do limite" ali seria
      // afirmar uma regra da NR-15 numa medição da NR-16.
      if (m.riskIsHazardous) {
        return (
          <Chip
            label={m.hazardousFraming ? 'Enquadrado na NR-16' : 'Não enquadrado'}
            color={m.hazardousFraming ? 'warning' : 'default'}
            variant={m.hazardousFraming ? 'filled' : 'outlined'}
            size="small"
          />
        );
      }

      return chipDaSituacao(m);
    },
  },
  {
    id: 'equipment',
    label: 'Equipamento',
    render: (m) => (
      <Typography variant="body2">{m.measurementEquipmentName ?? '—'}</Typography>
    ),
  },
  {
    id: 'responsible',
    label: 'Responsável',
    render: (m) => <Typography variant="body2">{m.responsibleUserName ?? '—'}</Typography>,
  },
];

const toFilters = (data: EnvironmentalMeasurementFiltersData): EnvironmentalMeasurementFilters => {
  const filters: EnvironmentalMeasurementFilters = {};

  if (data.searchTerm) filters.search = data.searchTerm;
  if (data.companyId) filters.companyId = Number(data.companyId);
  if (data.departmentId) filters.departmentId = Number(data.departmentId);
  if (data.occupationId) filters.occupationId = Number(data.occupationId);
  if (data.riskId) filters.riskId = Number(data.riskId);
  if (data.measurementEquipmentId) {
    filters.measurementEquipmentId = Number(data.measurementEquipmentId);
  }
  if (data.startDate) filters.startDate = data.startDate;
  if (data.endDate) filters.endDate = data.endDate;
  if (data.exceedsLimit) filters.exceedsLimit = data.exceedsLimit === 'true';
  if (data.hazardousFraming) filters.hazardousFraming = data.hazardousFraming === 'true';

  return filters;
};

export const EnvironmentalMeasurementListPage: React.FC = () => {
  const navigate = useNavigate();
  const criar = useVeredito('/environmental-measurements/new', pode('environmental_measurement.create'));
  const editar = useControle('/environmental-measurements/:id/edit', pode('environmental_measurement.update'), 'Editar');
  const excluir = useAcao('environmental_measurement.delete', 'Excluir');
  const riscos = useVeredito('/occupational-risks', pode('occupational_risk.read'));
  const equipamentos = useVeredito('/measurement-equipments', pode('measurement_equipment.read'));
  const { effectiveCompanyId } = useCompanyContext();

  const podeCriar = criar.liberado;
  const podeVerRiscos = riscos.liberado;
  const podeVerEquipamentos = equipamentos.liberado;

  // "Opera em uma empresa só?" é a perspectiva do contexto ativo, não o papel de quem olha:
  // no contexto Plataforma enxerga-se todas as empresas, e aí não há uma de que falar.
  const empresaUnica = effectiveCompanyId !== null;

  const [measurements, setMeasurements] = useState<EnvironmentalMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<EnvironmentalMeasurementFilters>({});
  const [filtersKey, setFiltersKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [companies, setCompanies] = useState<NamedOption[]>([]);
  const [departments, setDepartments] = useState<NamedOption[]>([]);
  const [occupations, setOccupations] = useState<NamedOption[]>([]);
  const [equipments, setEquipments] = useState<NamedOption[]>([]);
  const [risks, setRisks] = useState<NamedOption[]>([]);
  const [riskCount, setRiskCount] = useState<number | null>(null);

  const hasActiveFilters = Object.keys(apiFilters).length > 0;

  const loadMeasurements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await environmentalMeasurementService.getEnvironmentalMeasurements({
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      });

      setMeasurements(response.measurements);
      setTotal(response.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar medições ambientais');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadMeasurements();
  }, [loadMeasurements]);

  useEffect(() => {
    const loadOptions = async () => {
      const [companyList, departmentList, occupationList, equipmentList] = await Promise.all([
        companyService.getCompaniesForDropdown().catch(() => []),
        departmentService.getDepartments({ limit: 1000 }).catch(() => null),
        occupationService.getOccupations({ limit: 1000 }).catch(() => null),
        // Sem a permissão de leitura, o filtro de equipamento não se oferece — e a
        // tela não pede uma lista que a API vai recusar.
        podeVerEquipamentos
          ? measurementEquipmentService.getMeasurementEquipments({ limit: 1000 }).catch(() => null)
          : Promise.resolve(null),
      ]);

      setCompanies(companyList.map((c) => ({ id: c.id, name: c.name })));
      setDepartments((departmentList?.data ?? []).map((d) => ({ id: Number(d.id), name: d.name })));
      setOccupations((occupationList?.data ?? []).map((o) => ({ id: Number(o.id), name: o.name })));
      setEquipments((equipmentList?.equipments ?? []).map((e) => ({ id: e.id, name: e.name })));
    };

    loadOptions();
  }, [podeVerEquipamentos]);

  // O risco só é consultado por quem enxerga a tela de Riscos Ocupacionais —
  // é o que decide se o vazio pode apontar aquele caminho.
  useEffect(() => {
    if (!podeVerRiscos) return;

    const loadRisks = async () => {
      try {
        const response = await occupationalRiskService.getOccupationalRisks({
          limit: 1000,
          status: OccupationalRiskStatus.ACTIVE,
        });
        const list = response.risks ?? [];
        setRisks(list.map((r) => ({ id: r.id, name: r.riskFactorName })));
        setRiskCount(response.total ?? list.length);
      } catch {
        setRiskCount(null);
      }
    };

    loadRisks();
  }, [podeVerRiscos]);

  const handleFilter = useCallback((data: EnvironmentalMeasurementFiltersData) => {
    setApiFilters(toFilters(data));
    setPage(0);
  }, []);

  const handleClearFilters = () => {
    setApiFilters({});
    setPage(0);
    setFiltersKey((key) => key + 1);
  };

  const handleDelete = async (measurement: EnvironmentalMeasurement) => {
    if (!window.confirm('Tem certeza que deseja excluir esta medição ambiental?')) return;

    try {
      await environmentalMeasurementService.deleteEnvironmentalMeasurement(measurement.id);
      loadMeasurements();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir medição ambiental');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await environmentalMeasurementService.exportEnvironmentalMeasurements(apiFilters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'medicoes-ambientais.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao exportar medições ambientais');
    } finally {
      setExporting(false);
    }
  };

  const semPreRequisito = podeVerRiscos && riskCount === 0;

  const emptyState = useMemo(() => {
    if (hasActiveFilters) {
      return {
        title: 'Nenhuma medição encontrada para os filtros aplicados',
        description: 'Ajuste os filtros ou use "Limpar filtros" para ver todas as medições.',
      };
    }

    const escopo = empresaUnica ? ' nesta empresa' : ' no seu acesso';
    const title = `Nenhuma medição ambiental registrada${escopo}`;

    // Sem permissão de criar, o vazio não convida a uma ação que o usuário não tem como executar.
    if (!podeCriar) {
      return {
        title,
        description: 'Esta lista reúne os valores medidos em campo para cada aplicação de risco.',
      };
    }

    if (semPreRequisito) {
      return {
        title,
        description:
          'Antes de medir é preciso ter um risco ocupacional aplicado a uma empresa, setor, função ou funcionário — o cadastro e a aplicação do risco ficam em Riscos Ocupacionais.',
      };
    }

    return {
      title,
      description:
        'Esta lista reúne os valores medidos em campo para cada aplicação de risco: registre a primeira em "Nova Medição". As medições entram no PGR, no LTCAT, nos laudos e no PCA.',
    };
  }, [hasActiveFilters, empresaUnica, semPreRequisito, podeCriar]);

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Medições Ambientais' }]}
      title="Medições Ambientais"
      subtitle="Consulte, filtre e exporte os valores medidos em campo por aplicação de risco"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/environmental-measurements/new"
            exige={pode('environmental_measurement.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Nova Medição
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!podeCriar && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Você pode consultar e exportar as medições, mas não registrar novas. {criar.explicacao}
        </Alert>
      )}

      {semPreRequisito && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/occupational-risks')}
            >
              Ir para Riscos Ocupacionais
            </Button>
          }
        >
          A medição parte de um risco ocupacional já aplicado a uma empresa, setor, função ou
          funcionário. Não há nenhum risco cadastrado ainda.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <EnvironmentalMeasurementFiltersComponent
          key={filtersKey}
          onFilter={handleFilter}
          companies={companies}
          departments={departments}
          occupations={occupations}
          risks={risks}
          equipments={equipments}
          loading={loading}
          total={total}
        />
      </Box>

      {hasActiveFilters && !loading && measurements.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearFilters}>
              Limpar filtros
            </Button>
          }
        >
          Os filtros aplicados não devolveram nenhuma medição.
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={measurements}
        getRowId={(measurement) => measurement.id}
        loading={loading}
        emptyState={emptyState}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: (measurement) => navigate(`/environmental-measurements/${measurement.id}/edit`),
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDelete,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />
    </PageShell>
  );
};

export default EnvironmentalMeasurementListPage;
